import { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { OrganizationSelect } from '../../../components/common/OrganizationSelect/OrganizationSelect';
import { useOrganizationQuery } from '../../../hooks/useDashboardQueries';
import {
  useMigrationsQuery,
  usePreviewMigrationMutation,
  useRunMigrationMutation,
  useSystemBusy,
} from '../../../hooks/useSystemQueries';
import { MigrationInfo, MigrationOptions } from '../../../types/system';
import { parseApiError } from '../../../utils/parseApiError';
import { RowsTable } from './RowsTable';
import { RunHistory } from './RunHistory';

const errorText = (error: unknown) => (error ? parseApiError(error).message : undefined);

/** Preview, then Run, for each migration; the run history below. */
export const MigrationsTab = () => {
  const { t } = useTranslation();
  const migrations = useMigrationsQuery();
  const preview = usePreviewMigrationMutation();
  const run = useRunMigrationMutation();
  const busy = useSystemBusy();
  const [organizationId, setOrganizationId] = useState('');
  const chosenOrganization = useOrganizationQuery(organizationId || undefined);
  const [previewed, setPreviewed] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<MigrationInfo | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const optionsFor = (m: MigrationInfo): MigrationOptions =>
    m.input === 'organization' ? { organizationId } : {};
  const ready = (m: MigrationInfo) => m.input !== 'organization' || Boolean(organizationId);

  const doPreview = (m: MigrationInfo) => {
    setDone(null);
    setPreviewed(m.id);
    preview.mutate({ id: m.id, options: optionsFor(m) });
  };

  const doRun = async () => {
    const migration = confirming;
    setConfirming(null);
    if (!migration) return;
    try {
      await run.mutateAsync({ id: migration.id, options: optionsFor(migration) });
      setDone(migration.title);
      setPreviewed(null);
      preview.reset();
    } catch {
      // Shown from run.error.
    }
  };

  if (migrations.isLoading) return null;

  return (
    <div className="system-tab">
      {migrations.error && (
        <p className="role-form-error" role="alert">{errorText(migrations.error)}</p>
      )}
      {(migrations.data ?? []).map((m) => (
        <section key={m.id} className="team-panel">
          <header className="team-panel-header">
            <h3 className="team-panel-title">{m.title}</h3>
          </header>
          <div className="system-panel-body">
            <p className="team-muted">{m.description}</p>
            <div className="system-actions">
              {m.input === 'organization' && (
                <div className="system-org-select">
                  <OrganizationSelect
                    value={organizationId || undefined}
                    onChange={(id) => {
                      setOrganizationId(id ?? '');
                      if (previewed === m.id) setPreviewed(null);
                    }}
                    allowNone={false}
                    disabled={busy}
                    label={t('dashboard.system.migrations.organization')}
                    placeholder={t('dashboard.system.migrations.chooseOrganization')}
                  />
                </div>
              )}
              <button
                type="button"
                className="secondary-action-btn"
                disabled={!ready(m) || busy || preview.isPending}
                onClick={() => doPreview(m)}
              >
                {t('dashboard.system.migrations.preview')}
              </button>
              {previewed === m.id && preview.data && (
                <button
                  type="button"
                  className="primary-action-btn"
                  disabled={busy}
                  onClick={() => setConfirming(m)}
                >
                  {t('dashboard.system.migrations.run')}
                </button>
              )}
            </div>

            {previewed === m.id && preview.error && (
              <p className="role-form-error" role="alert">{errorText(preview.error)}</p>
            )}
            {previewed === m.id && preview.data && (
              <div className="system-preview">
                {preview.data.warnings.map((w) => (
                  <p key={w} className="role-form-error" role="alert">{w}</p>
                ))}
                <ul className="system-summary">
                  {Object.entries(preview.data.summary).map(([k, v]) => (
                    <li key={k}>
                      <strong>{v}</strong> {k}
                    </li>
                  ))}
                </ul>
                <RowsTable rows={preview.data.rows} />
              </div>
            )}
            {previewed === m.id && run.isPending && (
              <p className="team-muted" role="status">{t('dashboard.system.migrations.running')}</p>
            )}
          </div>
        </section>
      ))}

      {run.error && <p className="role-form-error" role="alert">{errorText(run.error)}</p>}
      {done && (
        <p className="system-success" role="status">
          {t('dashboard.system.migrations.done', { name: done })}
        </p>
      )}

      <RunHistory kind="migration" />

      <Dialog open={Boolean(confirming)} onClose={() => setConfirming(null)}>
        <DialogTitle>{t('dashboard.system.migrations.confirmTitle')}</DialogTitle>
        <DialogContent>
          <p>{confirming?.description}</p>
          {confirming?.input === 'organization' && (
            <p>
              <strong>
                {t('dashboard.system.migrations.confirmOrganization', {
                  name: chosenOrganization.data?.name || organizationId,
                })}
              </strong>
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <button type="button" className="secondary-action-btn" onClick={() => setConfirming(null)}>
            {t('common.cancel')}
          </button>
          <button type="button" className="primary-action-btn" disabled={busy} onClick={() => void doRun()}>
            {t('dashboard.system.migrations.run')}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
